import { FC, useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';
import { useGetDatas, useGetViewDatas } from 'modules/account/settings/hooks';
import { SettingIcon } from 'ui/components/icon';
import { useExternalModal } from 'ui/partials/Modal/useModal';
import { ACTIONS, PERMISSION_KEYS } from 'utils/constants';
import { cn } from 'utils/helpers';
import { Data, DATA_SOURCE, DATA_TYPE, VIEWDATA_TYPE } from 'utils/types';
import { isNil } from 'lodash';
import { DEAL_FORM } from 'modules/account/sales/forms/schema';
import { useCan } from 'utils/providers/RolePermission';

import { COMPANY_FORM, LEAD_FORM } from './schema';
import { CustomFieldsModal } from '../components';
import BaseCustomFieldFormField from './BaseCustomFieldFormField';

type Props = {
  source: DATA_SOURCE;
};

const BaseCustomFieldsForm: FC<Props> = ({ source }) => {
  const canUpdateCustomFields = useCan(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.VIEW_DATA_FORM,
  );

  const { setFieldValue, values, isSubmitting } =
    useFormikContext<
      typeof source extends DATA_SOURCE.COMPANY
        ? COMPANY_FORM
        : typeof source extends DATA_SOURCE.DEAL
          ? DEAL_FORM
          : LEAD_FORM
    >();
  const { showModal, hideModal } = useExternalModal();
  const { data: customFields } = useGetDatas({
    source,
  });

  const { data: companiesCustomFields } = useGetViewDatas({
    type: VIEWDATA_TYPE.FORM,
    source,
  });

  const dataIds = useMemo(() => {
    const storedDataIds = companiesCustomFields?.data?.[0]?.data;

    if (customFields?.data && storedDataIds) {
      return customFields?.data
        .filter((field) =>
          storedDataIds.map((id) => id.dataId).includes(field._id),
        )
        .map((field) => ({
          ...field,
          sequence:
            storedDataIds.find((id) => id.dataId === field._id)?.sequence ?? 0,
        }))
        .sort((a, b) => a.sequence - b.sequence);
    } else {
      return [];
    }
  }, [customFields?.data, companiesCustomFields?.data]);

  const handleAddFields = () => {
    showModal({
      content: (
        <CustomFieldsModal
          source={source}
          usedOn={VIEWDATA_TYPE.FORM}
          onClose={hideModal}
        />
      ),
      size: 'xl',
      disableOutsideClick: true,
      resetPadding: true,
      title: 'Custom Fields',
    });
  };

  const handleUpdateCustomField = useCallback(
    (dataId: string, value: any, field: Data) => {
      const datas = values?.data ?? [];

      const fieldIndex = datas.findIndex((dt) => dt.dataId === dataId);

      let text = '';
      if (field.type === DATA_TYPE.STRING) {
        //Check min/max length
        if (field.meta.min && value.length && value.length < field.meta.min) {
          text = `Min length of ${field.meta.min} characters required`;
        }
        if (field.meta.max && value.length > field.meta.max) {
          text = `Max length of ${field.meta.max} characters required`;
        }
      }
      if (field.type === DATA_TYPE.NUMBER) {
        //Check min/max length
        if (field.meta.min && !isNil(value) && value < field.meta.min) {
          text = `Min value of ${field.meta.min} required`;
        }
        if (field.meta.max && value > field.meta.max) {
          text = `Max value of ${field.meta.max} required`;
        }
      }

      if (fieldIndex > -1) {
        datas[fieldIndex] = { dataId, value, error: text };
      } else {
        datas.push({ dataId, value, error: text });
      }

      setFieldValue('data', datas);
    },
    [setFieldValue, values?.data],
  );

  return (
    <div
      className={cn(
        'space-y-5',
        'pt-5 max-h-[calc(100vh-280px)] overflow-auto',
      )}
    >
      {canUpdateCustomFields && (
        <button
          className="flex gap-1.5 items-center text-sm"
          type="button"
          onClick={handleAddFields}
        >
          <SettingIcon className="h-[18px] w-[18px] text-content" />
          Add custom fields
        </button>
      )}
      {dataIds?.map((field) => (
        <BaseCustomFieldFormField
          key={field._id}
          handleUpdateCustomField={handleUpdateCustomField}
          field={field}
          customData={values.data}
          disabled={isSubmitting}
        />
      ))}
    </div>
  );
};

export default BaseCustomFieldsForm;
